import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CreateCrmDealTaskPayload, CrmTask, TaskQuickFilter, TaskStatus } from "types/crm/task";
import { usePaginatedList } from "../usePaginatedList";
import { dealApi, dealQueryKeys, UpdateCrmDealTaskPayload } from "service/crm/api/deal";
import { useEntityCreateMutation } from "../useEntityMutation";


export const useDealTasks = (
  dealUuid: string,
  pageSize = 20,
  status?: TaskStatus,
  quickFilter?: TaskQuickFilter,
) => {
  const { items, ...rest } = usePaginatedList<CrmTask>(
    dealQueryKeys.tasks(dealUuid, status, quickFilter),
    (page) => dealApi.getDealTasks(dealUuid, page, pageSize, status, quickFilter),
    !!dealUuid,
  );
  return { tasks: items, ...rest };
};

/** This deal's tasks that are past due and not completed. */
export const useDealOverdueTasks = (dealUuid: string, pageSize = 20) =>
  useDealTasks(dealUuid, pageSize, undefined, "OVERDUE");

export const useCreateDealTaskMutation = () =>
  useEntityCreateMutation<CreateCrmDealTaskPayload, CrmTask>(
    (payload) => dealApi.createDealTask(payload),
    (variables) => [
      [...dealQueryKeys.all, "tasks", variables.dealUuid],
      dealQueryKeys.activities(variables.dealUuid),
    ],
  );

export const useUpdateDealTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateCrmDealTaskPayload) =>
      dealApi.updateDealTask(payload),
    onSuccess: (_data, variables) => {
      // Prefix-match invalidation: clears the cache for every status filter
      // of this deal's task list, not just "ALL".
      queryClient.invalidateQueries({
        queryKey: [...dealQueryKeys.all, "tasks", variables.dealUuid],
      });
      queryClient.invalidateQueries({
        queryKey: dealQueryKeys.activities(variables.dealUuid),
      });
    },
  });
};

export const useDeleteDealTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dealUuid,
      taskUuid,
    }: {
      dealUuid: string;
      taskUuid: string;
    }) => dealApi.deleteDealTask(dealUuid, taskUuid),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...dealQueryKeys.all, "tasks", variables.dealUuid],
      });
      queryClient.invalidateQueries({
        queryKey: dealQueryKeys.activities(variables.dealUuid),
      });
    },
  });
};